// track/utils.ts

/**
 * Parses a date string by attempting to auto-detect the format (DD/MM/YYYY vs MM/DD/YYYY).
 * @param dateStr The date string to parse.
 * @returns A Date object or null if parsing fails.
 */
export const parseDateString = (dateStr: string): Date | null => {
    if (!dateStr || !dateStr.trim()) return null;
  
    const trimmedDateStr = dateStr.trim();
  
    // Handle slash-separated dates, which are the most ambiguous
    if (trimmedDateStr.includes('/')) {
      const parts = trimmedDateStr.split('/');
      if (parts.length === 3) {
        const part1 = parseInt(parts[0], 10);
        const part2 = parseInt(parts[1], 10);
        let year = parseInt(parts[2], 10);
  
        if (isNaN(part1) || isNaN(part2) || isNaN(year)) {
          return null;
        }
  
        // Handle two-digit years (e.g., '25' -> 2025)
        if (year < 100) {
          year += 2000;
        }
  
        let day: number, month: number;
  
        // Intelligent format detection
        if (part1 > 12 && part2 <= 12) {
          // Format is clearly DD/MM/YYYY
          day = part1;
          month = part2;
        } else if (part1 <= 12 && part2 > 12) {
          // Format is clearly MM/DD/YYYY
          day = part2;
          month = part1;
        } else {
          // Ambiguous case (e.g., 05/10/2025). We will assume DD/MM/YYYY
          // as a safer default for non-US data. This could be a user setting in the future.
          day = part1;
          month = part2;
        }
  
        // JavaScript months are 0-indexed (0 = January)
        const date = new Date(Date.UTC(year, month - 1, day));
        
        // Final validation to ensure we created a valid date
        if (!isNaN(date.getTime()) && date.getUTCDate() === day && date.getUTCMonth() === month - 1) {
          return date;
        }
      }
    }
  
    // Fallback for other standard formats like YYYY-MM-DD that new Date() handles correctly
    const fallbackDate = new Date(trimmedDateStr);
    if (!isNaN(fallbackDate.getTime())) {
      return fallbackDate;
    }
  
    return null; // Return null if all parsing attempts fail
  };
  
  
  /**
   * Formats an organization's name for display purposes.
   * @param name The original name of the organization.
   * @returns The formatted name.
   */
  export const formatOrganizationName = (name: string): string => {
    if (name === 'DINEUKFINALMILE') return 'Dine UK Final Mile';
    if (name === 'DINEUSA99') return 'Dine USA 99';
    return name.toLowerCase().replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, str => str.toUpperCase());
  };
  
  /**
   * Parses a single line from a CSV file, handling quoted values.
   * @param line The CSV line to parse.
   * @returns An array of strings representing the fields in the line.
   */
  export const parseCSVLine = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
  
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      const nextChar = line[i + 1];
  
      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        result.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current);
    return result;
  };
  
  
  /**
   * Calculates the similarity between two strings using the Levenshtein distance.
   * @param str1 The first string.
   * @param str2 The second string.
   * @returns A number between 0 and 1, where 1 means the strings are identical.
   */
  export const calculateSimilarity = (str1: string, str2: string): number => {
    const longer = str1.length > str2.length ? str1 : str2;
    const shorter = str1.length > str2.length ? str2 : str1;
  
    if (longer.length === 0) return 1.0;
  
    const editDistance = getEditDistance(longer, shorter);
    return (longer.length - editDistance) / longer.length;
  };
  
  /**
   * Calculates the Levenshtein distance between two strings.
   * @param str1 The first string.
   * @param str2 The second string.
   * @returns The number of edits required to change one string into the other.
   */
  export const getEditDistance = (str1: string, str2: string): number => {
    const matrix = Array(str2.length + 1).fill(null).map(() => Array(str1.length + 1).fill(null));
  
    for (let i = 0; i <= str1.length; i++) matrix[0][i] = i;
    for (let j = 0; j <= str2.length; j++) matrix[j][0] = j;
  
    for (let j = 1; j <= str2.length; j++) {
      for (let i = 1; i <= str1.length; i++) {
        const cost = str1[i - 1] === str2[j - 1] ? 0 : 1;
        matrix[j][i] = Math.min(
          matrix[j - 1][i] + 1,      // deletion
          matrix[j][i - 1] + 1,      // insertion
          matrix[j - 1][i - 1] + cost  // substitution
        );
      }
    }
  
    return matrix[str2.length][str1.length];
  };
  
  /**
   * Creates a SHA-256 hash of a string.
   * @param str The string to hash.
   * @returns A promise that resolves to the hex-encoded hash string.
   */
  const sha256 = async (str: string): Promise<string> => {
    const textAsBuffer = new TextEncoder().encode(str);
    const hashBuffer = await crypto.subtle.digest('SHA-256', textAsBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    return hashHex;
  }
  
  /**
   * Hashes a CSV row to create a unique identifier.
   * @param row An array of strings representing the fields in a CSV row.
   * @returns A promise that resolves to the SHA-256 hash of the concatenated row data.
   */
  export const hashCSVRow = async (row: string[]): Promise<string> => {
    const rowString = row.join(',');
    return await sha256(rowString);
  };